import React, { FC, Fragment } from 'react';
import { NavLink } from 'react-router-dom';

export interface SideNavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Compteur optionnel affiché à droite du libellé. */
  badge?: number;
  /** Cible de navigation (chemin relatif à la route courante). Présent = l'entrée
      est un NavLink et l'état actif dérive de l'URL ; absent = bouton + activeId. */
  to?: string;
}

interface SideNavMenuProps {
  items: SideNavItem[];
  /** Mode bouton uniquement — inutile quand les items portent un `to`. */
  activeId?: string;
  onSelect?: (id: string) => void;
}

interface EntryContentProps {
  item: SideNavItem;
  active: boolean;
}

const EntryContent: FC<EntryContentProps> = ({ item, active }) => {
  const Icon = item.icon;
  return (
    <>
      <div
        className={`flex-shrink-0 transition-all duration-300 ${
          active
            ? 'text-primary transform scale-110'
            : 'text-gray-500 group-hover:text-primary group-hover:transform group-hover:scale-110'
        }`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <span
        className={`text-sm truncate transition-all duration-300 ${
          active ? 'text-primary font-bold' : 'text-gray-500 font-semibold'
        }`}
      >
        {item.label}
      </span>
      {item.badge !== undefined && (
        <span
          className={`ml-auto rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${
            active ? 'bg-primary text-white' : 'bg-primary/10 text-primary'
          }`}
        >
          {item.badge}
        </span>
      )}
    </>
  );
};

const ENTRY_CLASSES =
  'w-full flex items-center gap-4 px-3 py-3 text-left transition-all duration-300 group';

/**
 * Menu latéral des pages de détail (fiche plateforme, atelier des rôles…) :
 * cartouche teinté primary, une entrée par section, l'entrée active en primary
 * gras avec l'icône légèrement grossie. Deux modes par item : lien (`to`,
 * l'URL est la source de vérité — précédent/suivant et deep links marchent)
 * ou bouton (`activeId`/`onSelect`).
 */
export const SideNavMenu: FC<SideNavMenuProps> = ({
  items,
  activeId,
  onSelect
}) => (
  <div className="bg-primary/5 rounded-3xl shadow-sm overflow-hidden sticky top-6 border border-primary/10">
    <nav className="p-2">
      {items.map((item, index) => (
        <Fragment key={item.id}>
          {index > 0 && <div className="border-b border-primary/10" />}
          {item.to != null ? (
            <NavLink to={item.to} className={ENTRY_CLASSES}>
              {({ isActive }) => <EntryContent item={item} active={isActive} />}
            </NavLink>
          ) : (
            <button
              type="button"
              onClick={() => onSelect?.(item.id)}
              className={ENTRY_CLASSES}
            >
              <EntryContent item={item} active={item.id === activeId} />
            </button>
          )}
        </Fragment>
      ))}
    </nav>
  </div>
);
